import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { ELayoutActions, SetMainMenu, SetTabMenu } from "@edp-store/actions/layout.actions";
import { Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { withLatestFrom, switchMap, mergeMap } from "rxjs/operators";
import { getActiveTabMenu } from "@edp-store/selectors/layout.selectors";
import { Menu, MenuResponse } from "@edp-models/menu.model";
import { of } from "rxjs";
import { getRouterState } from "@edp-store/selectors/router.selectors";
import { RouterStateUrl } from "@edp-utils/route-serializer";
import { RouterReducerState } from "@ngrx/router-store";
import { MenuService } from "@edp-services/menu.service";
import { environment } from "@edp-environments/environment";
import { RegionService } from "@edp-services/region.service";
import { RegionEnum } from "@edp-enums/region.enum";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { getContractEs } from "@edp-store/selectors/ES/contract-es.selectors";
import { ContractEs } from "@edp-models/ES/es-contract.model";
import { ContractTypeEsEnum } from "@edp-enums/ES/contract-type-es-enum";

const initalRoute = "/energia/inicio";
const initialRouteSplit = initalRoute.split("/").filter(i => !!i);

@Injectable()
export class LayoutEffects {
    @Effect()
    refreshMenu$ = this.actions$.pipe(
        ofType(ELayoutActions.RefreshMainMenu),
        switchMap(() => this.menuService.getMenu()),
        withLatestFrom(this.store.select(getRouterState), this.store.select(getActiveTabMenu), this.storeES.select(getContractEs)),
        mergeMap(([menu, router, activeTab, esContract]: [MenuResponse[], RouterReducerState<RouterStateUrl>, string, ContractEs]) => {
            const type = esContract?.type;
            const convertMenu = this.menuService.convertMenu(menu);
            const menuList = this.isSpain ? this.spainMenu(convertMenu, type) : convertMenu;

            const currentRoute = router.state.url.replace(/\?.*/g, "");

            this.activateMenu(menuList, currentRoute, activeTab);

            // Fallback for grouping
            let activeMenu = menuList.find(menuItem => menuItem.isActive);
            if (!activeMenu) {
                this.activateMenu(menuList, currentRoute, activeTab, true);
                activeMenu = menuList.find(menuItem => menuItem.isActive);
            }

            if (!activeTab && activeMenu) {
                const activeSubMenu = activeMenu.submenu.find(menuItem => menuItem.isActive);
                if (activeSubMenu && activeSubMenu.tab) {
                    return of(new SetTabMenu(activeSubMenu.tab));
                }
            }

            return of(new SetMainMenu(menuList));
        })
    );

    constructor(
        private actions$: Actions,
        private store: Store<IAppState>,
        private readonly menuService: MenuService,
        private region: RegionService,
        private readonly storeES: Store<IAppEsState>
    ) {}
    private isSpain = this.region.getRegion() === RegionEnum.ES && true;

    /** check route energia/inicio */
    private checkInitialRoute(routerPath: Array<string>): boolean {
        return initialRouteSplit.every(path => routerPath?.length > 0 && routerPath[0].includes(path));
    }

    private activateMenu(menu: Menu[], routeUrl: string, activeTab: string, grouping: boolean = false): Menu[] {
        return menu.map(menuItem => {
            menuItem.isActive = menuItem.routerPath && menuItem.routerPath[0] === routeUrl;

            if (grouping && menuItem.grouping && routeUrl.indexOf(menuItem.grouping) >= 0) {
                menuItem.isActive = true;
            }

            this.activateMenu(menuItem.submenu, routeUrl, activeTab, grouping);

            if (!menuItem.isActive) {
                let itemActive = menuItem.submenu.some(subMenuItem => subMenuItem.isActive);
                if (!itemActive && routeUrl === initalRoute) {
                    itemActive = this.checkInitialRoute(menuItem.routerPath);
                }
                menuItem.isActive = itemActive;
            }
            if (environment.isApp && menuItem.isActive && !menuItem.submenu.length && menuItem.tab && activeTab) {
                menuItem.isActive = menuItem.tab === activeTab;
            }

            return menuItem;
        });
    }

    private spainMenu(menu: Menu[], type: string): Menu[] {
        if (type === ContractTypeEsEnum.SOLAR) {
            return menu.filter(menuItem => menuItem.name !== "MENU.ENERGY-E");
        } else {
            return menu.filter(menuItem => menuItem.name !== "MENU.ENERGY-SOLAR");
        }
    }
}
